import { useCallback, useEffect, useState } from "react";
import {
  fetchManageableTenants,
  getSelectedTenant,
  setSelectedTenant,
} from "../helpers/tenants";
import type { TenantInfo } from "../helpers/tenants";

/**
 * ADR-021 tenant selection shared by backoffice pages.
 *
 * Loads the server-derived grant list (/auth/me/tenants), keeps the current
 * choice in sync with localStorage (helpers/tenants) — the api interceptor
 * forwards it as `X-Selected-Tenant-ID`, and the api-gw enforces it. Pages that
 * fetch tenant-scoped data should re-run their fetch from the `onChange` they
 * pass to <TenantSelector/>.
 *
 * Defaults to the first granted tenant only when nothing is stored yet. The
 * api-gw prepends the operator's resolved tenant, so the first entry is that
 * tenant (e.g. "niposom"), not arbitrary.
 */
export function useTenantSelection() {
  const [tenants, setTenants] = useState<TenantInfo[]>([]);
  const [selectedTenant, setSelectedTenantState] = useState<string>(
    () => getSelectedTenant() ?? "",
  );

  useEffect(() => {
    let active = true;
    fetchManageableTenants().then((list) => {
      if (!active) return;
      setTenants(list);
      if (!getSelectedTenant() && list.length > 0) {
        setSelectedTenant(list[0].id);
        setSelectedTenantState(list[0].id);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  const selectTenant = useCallback((tenantId: string) => {
    setSelectedTenant(tenantId);
    setSelectedTenantState(tenantId);
  }, []);

  return { tenants, selectedTenant, selectTenant };
}

export default useTenantSelection;
